// src/pages/clientes.js
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Col, Container, Row, Button, Spinner, Alert } from "reactstrap";
import ClientList from "../components/Client/ClientList";
import NewClientModal from "../components/Client/NewClientModal";
import axios from "axios";
import { getAuthHeaders } from "../utils/auth";
import { API_URL, API_URL_sedes } from "../constants";
import Papa from "papaparse";
import Moment from "moment";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Link } from "react-router-dom";

// Utility functions extracted for better organization
const CsvUtils = {
  downloadWithBOM: (data, filename, delimiter = ",") => {
    if (!data?.length) {
      toast.info("No hay datos para exportar.");
      return;
    }

    const csv = Papa.unparse(data, {
      delimiter,
      quotes: true,
      newline: "\r\n",
      header: true,
    });

    const csvWithBOM = "\uFEFF" + csv;
    const blob = new Blob([csvWithBOM], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url); // Clean up memory
  },

  normalizeDate: (value) => {
    if (!value) return value;

    const m = Moment(
      value,
      [
        Moment.ISO_8601,
        "YYYY-MM-DD",
        "M/D/YYYY",
        "DD/MM/YYYY",
        "MM/DD/YYYY",
        "YYYY/MM/DD",
      ],
      true
    );

    return m.isValid() ? m.format("YYYY-MM-DD") : value;
  },

  normalizeDates: (row) => {
    const normalized = { ...row };
    Object.keys(normalized).forEach((key) => {
      if (normalized[key]) {
        normalized[key] = CsvUtils.normalizeDate(normalized[key]);
      }
    });
    return normalized;
  },

  validateFile: (file) => {
    const MAX_SIZE = 10 * 1024 * 1024; // 10MB
    const ALLOWED_TYPES = ['text/csv', 'application/vnd.ms-excel'];

    if (!file) {
      return { valid: false, error: "No se seleccionó archivo" };
    }

    if (file.size > MAX_SIZE) {
      return { valid: false, error: "El archivo es demasiado grande (máx 10MB)" };
    }

    if (!ALLOWED_TYPES.includes(file.type) && !file.name.endsWith('.csv')) {
      return { valid: false, error: "Solo se permiten archivos CSV" };
    }

    return { valid: true };
  },
};

const Clientes = () => {
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [sedes, setSedes] = useState([]);
  const [error, setError] = useState(null);
  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  
  const fileInputRef = useRef(null);

  // Fetch clients with better error handling
  const getClients = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    const headers = getAuthHeaders();

    try {
      const res = await axios.get(API_URL, {
        headers,
        timeout: 10000, // 10 second timeout
      });

      setClients(res.data || []);
      setFilteredClients([]);
    } catch (err) {
      console.error("Error fetching clients:", err);
      
      const errorMsg = err.response?.status === 401
        ? "Sesión expirada. Por favor inicia sesión nuevamente."
        : err.response?.data?.message || "Error al cargar clientes";
      
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load Value Streams catalog
  const loadSedes = useCallback(async () => {
    const headers = getAuthHeaders();

    try {
      const res = await axios.get(API_URL_sedes, {
        headers,
        timeout: 10000,
      });

      setSedes(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Error fetching sedes:", err);
      toast.warn("No se pudieron cargar los Value Streams");
    }
  }, []);

  // Initial load
  useEffect(() => {
    getClients();
    loadSedes();
  }, [getClients, loadSedes]);

  // Reset/refresh data
  const resetState = useCallback(() => {
    getClients();
    loadSedes();
  }, [getClients, loadSedes]);

  // Handle filtered clients change
  const handleFilteredChange = useCallback((filteredRows) => {
    setFilteredClients(filteredRows || []);
  }, []);

  // Download CSV export
  const downloadCSV = useCallback(() => {
    const source = filteredClients.length ? filteredClients : clients;

    if (!source.length) {
      toast.info("No hay clientes para exportar.");
      return;
    }

    const fields = [
      "razon_social",
      "id_fiscal",
      "nombre",
      "giro",
      "value_stream",
      "nombre_comprador",
      "nombre_admin",
      "email",
      "phone",
      "pais",
      "codigo_postal",
      "estado",
      "ciudad",
      "calle",
      "client_code",
      "status",
    ];

    const normalized = source.map((client) => {
      const row = {};
      
      fields.forEach((field) => {
        if (field === "value_stream") {
          const sede = client?.sede;
          row.value_stream =
            typeof sede === "object" && sede !== null ? sede.sede ?? "" : "";
        } else {
          row[field] = client?.[field] ?? "";
        }
      });

      return row;
    });

    const dateStr = new Date().toISOString().slice(0, 10);
    CsvUtils.downloadWithBOM(normalized, `Clientes_${dateStr}.csv`);
    
    toast.success("CSV descargado exitosamente");
  }, [clients, filteredClients]);

  // Handle file selection
  const handleFileChange = useCallback((e) => {
    const selectedFile = e.target.files?.[0];
    
    if (!selectedFile) {
      setFile(null);
      return;
    }

    const validation = CsvUtils.validateFile(selectedFile);
    
    if (!validation.valid) {
      toast.error(validation.error);
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    setFile(selectedFile);
  }, []);

  // Map Value Stream name to ID
  const mapValueStream = useCallback((vsNameRaw) => {
    if (!vsNameRaw) return null;

    const vsName = typeof vsNameRaw === "string" ? vsNameRaw.trim() : "";
    
    if (!vsName) return null;

    const match = sedes.find(
      (s) =>
        typeof s?.sede === "string" &&
        s.sede.trim().toLowerCase() === vsName.toLowerCase()
    );

    return match || null;
  }, [sedes]);

  // Process CSV import
  const updateDatabaseFromCSV = useCallback(async (result) => {
    if (!sedes.length) {
      console.warn("No sedes loaded for value_stream mapping");
    }

    const unknownVS = new Set();
    let rows;

    try {
      rows = (result?.data || []).map((r) => {
        const row = CsvUtils.normalizeDates(r);

        // Handle value_stream mapping
        const vsNameRaw =
          row.value_stream ?? row.Value_Stream ?? row.valueStream;

        if (vsNameRaw) {
          const match = mapValueStream(vsNameRaw);
          
          if (match) {
            row.sede = match.id;
          } else {
            unknownVS.add(vsNameRaw);
            row.sede = null;
          }
        }

        // Remove CSV-only columns
        delete row.value_stream;
        delete row.Value_Stream;
        delete row.valueStream;

        return row;
      });
    } catch (err) {
      console.error("Error parsing CSV:", err);
      toast.error("Error al procesar el CSV. Verifica el formato.");
      return;
    }

    if (!rows.length) {
      toast.warn("El CSV está vacío.");
      return;
    }

    if (unknownVS.size > 0) {
      console.warn("Unknown Value Streams:", Array.from(unknownVS));
      toast.warn(
        `${unknownVS.size} Value Stream(s) no encontrado(s): ${Array.from(unknownVS).join(", ")}`
      );
    }

    // Process rows
    const duplicates = [];
    const otherErrors = [];
    let successCount = 0;

    const headers = getAuthHeaders();

    for (const [index, entry] of rows.entries()) {
      try {
        await axios.post(API_URL, entry, { headers });
        successCount++;
      } catch (err) {
        const errData = err?.response?.data || {};
        const isIdFiscalDuplicate =
          errData.id_fiscal?.some?.((msg) =>
            msg.toLowerCase().includes("already exists")
          );

        const rowInfo = {
          csvRow: index + 2,
          id_fiscal: entry.id_fiscal,
          razon_social: entry.razon_social,
          error: errData,
        };

        if (isIdFiscalDuplicate) {
          duplicates.push(rowInfo);
        } else {
          otherErrors.push(rowInfo);
        }
      }
    }

    // Summary
    if (successCount > 0) {
      toast.success(`✓ ${successCount} cliente(s) cargado(s) exitosamente`);
      resetState();
    }

    if (duplicates.length > 0) {
      console.table(duplicates);
      toast.warn(
        `⚠ ${duplicates.length} duplicado(s) por ID Fiscal (ver consola)`
      );
    }

    if (otherErrors.length > 0) {
      console.error("CSV import errors:", otherErrors);
      toast.error(
        `✗ ${otherErrors.length} error(es) al cargar (ver consola)`
      );
    }

    // Clear file input
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [sedes, mapValueStream, resetState]);

  // Handle file upload
  const handleFileUpload = useCallback(() => {
    if (!file) {
      toast.warn("Selecciona un archivo CSV primero");
      return;
    }

    setIsUploading(true);

    const reader = new FileReader();
    
    reader.onload = (event) => {
      Papa.parse(event.target.result, {
        header: true,
        skipEmptyLines: true,
        complete: (result) => {
          updateDatabaseFromCSV(result);
          setIsUploading(false);
        },
        error: (err) => {
          console.error("Papa parse error:", err);
          toast.error("Error al leer el CSV");
          setIsUploading(false);
        },
      });
    };

    reader.onerror = () => {
      console.error("FileReader error:", reader.error);
      toast.error("Error al leer el archivo");
      setIsUploading(false);
    };

    reader.onabort = () => {
      toast.error("Lectura de archivo cancelada");
      setIsUploading(false);
    };

    reader.readAsText(file);
  }, [file, updateDatabaseFromCSV]);

  return (
    <Container style={{ marginTop: "20px" }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <h1>Clientes</h1>
        <Link
          to="/clientes/importar"
          style={{
            color: "#dc3545",
            fontSize: "0.875rem",
            fontWeight: 500,
            textDecoration: "none",
            padding: "8px 14px",
            border: "1px solid #dc3545",
            borderRadius: 8,
          }}
        >
          📥 Importar Clientes
        </Link>
      </div>

      {error && (
        <Alert color="danger" className="mb-3">
          {error}
        </Alert>
      )}

      <Row className="mb-3">
        <Col className="d-flex gap-2 align-items-center flex-wrap">
          <Button
            color="info"
            onClick={downloadCSV}
            disabled={isLoading || !clients.length}
            aria-label="Descargar clientes en formato CSV"
          >
            📥 Descargar CSV
          </Button>

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            disabled={isUploading}
            aria-label="Seleccionar archivo CSV para importar"
          />
          
          <Button
            color="primary"
            onClick={handleFileUpload}
            disabled={!file || isUploading}
            aria-label="Subir archivo CSV seleccionado"
          >
            {isUploading ? (
              <>
                <Spinner size="sm" className="me-1" />
                Subiendo...
              </>
            ) : (
              "📤 Subir CSV"
            )}
          </Button>

          {file && (
            <small className="text-muted">
              Archivo: {file.name} ({(file.size / 1024).toFixed(1)} KB)
            </small>
          )}
        </Col>
      </Row>

      <hr />

      {isLoading ? (
        <div className="text-center py-5">
          <Spinner color="primary" />
          <p className="mt-2">Cargando clientes...</p>
        </div>
      ) : (
        <>
          <Row>
            <Col>
              <ClientList
                clients={clients}
                resetState={resetState}
                itemsPerPage={14}
                onFilteredChange={handleFilteredChange}
              />
            </Col>
          </Row>

          <Row>
            <Col>
              <NewClientModal create={true} resetState={resetState} />
            </Col>
          </Row>
        </>
      )}

      <ToastContainer position="bottom-right" autoClose={4000} />
    </Container>
  );
};

export default Clientes;